import * as React from 'react';
import {
  useDesignerSelector,
  useDispatch,
} from '@packages/systems/designer/flux';
import type {DesignerStores} from '@packages/systems/designer/flux';
import {
  isFormSettingsFetching,
  isFormSettingsUpdating,
  getFormSettingsUpdatingState,
  getFormSettingsError,
  getFormSettingsUiState,
  getFormSettings,
  fetchFormSettingsRequested,
  updateFormSettingsRequested,
  formSettingsUiStateUpdated,
  type LoadingState,
} from '@packages/systems/designer/forms/store';
import type {
  FormSettingsPayload,
  FormSettingsError,
} from '@packages/systems/designer/forms/types';

interface UseFormSettingsReturn {
  formSettings: FormSettingsPayload | null;
  isFetching: boolean;
  isUpdating: boolean;
  updateState: LoadingState;
  error: FormSettingsError | null;
  fetchSettings: () => void;
  updateSettings: (settings: FormSettingsPayload) => void;
  fetchUiState: {isWebflowConfigured: boolean; isEmailConfigured: boolean};
  updateUiState: (uiState: {
    isWebflowConfigured?: boolean;
    isEmailConfigured?: boolean;
  }) => void;
}

/**
 * Hook for managing form settings
 *
 * Provides a clean, type-safe interface for interacting with the FormSettingsStore.
 * Follows Webflow's hook patterns and integrates seamlessly with the Designer store.
 *
 * @param elementId - The element ID to manage settings for
 * @param componentId - Optional component ID for component-specific settings
 *
 * @example
 * ```tsx
 * const MyFormComponent = ({ elementId }) => {
 *   const {
 *     formSettings,
 *     isLoading,
 *     error,
 *     fetchSettings,
 *     updateSettings
 *   } = useFormSettings(elementId);
 *
 *   if (isLoading) return <Spinner />;
 *   if (error) return <ErrorMessage error={error} />;
 *
 *   return (
 *     <div>
 *       <FormSettingsEditor
 *         settings={formSettings}
 *         onUpdate={updateSettings}
 *       />
 *     </div>
 *   );
 * };
 * ```
 */
export const useFormSettings = (
  elementId: string,
  componentId: string | undefined
): UseFormSettingsReturn => {
  const dispatch = useDispatch();

  // Best practice is to use useSelectorFactory to create dynamic memoized selectors
  // But useSelectorFactory is only available in public/ so using useCallback instead
  const formSettingsSelector = React.useCallback(
    (state: DesignerStores) =>
      getFormSettings(state, elementId, componentId ?? ''),
    [elementId, componentId]
  );

  const fetchingSelector = React.useCallback(
    (state: DesignerStores) =>
      isFormSettingsFetching(state, elementId, componentId ?? ''),
    [elementId, componentId]
  );

  const updatingSelector = React.useCallback(
    (state: DesignerStores) =>
      isFormSettingsUpdating(state, elementId, componentId ?? ''),
    [elementId, componentId]
  );

  const updateStateSelector = React.useCallback(
    (state: DesignerStores) =>
      getFormSettingsUpdatingState(state, elementId, componentId ?? ''),
    [elementId, componentId]
  );

  const errorSelector = React.useCallback(
    (state: DesignerStores) =>
      getFormSettingsError(state, elementId, componentId ?? ''),
    [elementId, componentId]
  );

  const uiStateSelector = React.useCallback(
    (state: DesignerStores) =>
      getFormSettingsUiState(state, elementId, componentId ?? ''),
    [elementId, componentId]
  );

  // Get form settings from the designer store
  const formSettings = useDesignerSelector(formSettingsSelector);
  const isFetching = useDesignerSelector(fetchingSelector);
  const isUpdating = useDesignerSelector(updatingSelector);
  const updateState = useDesignerSelector(updateStateSelector);
  const error = useDesignerSelector(errorSelector);

  const fetchSettings = React.useCallback(() => {
    dispatch(fetchFormSettingsRequested({elementId, componentId}));
  }, [dispatch, elementId, componentId]);

  const updateSettings = React.useCallback(
    (settings: FormSettingsPayload) => {
      dispatch(updateFormSettingsRequested({elementId, componentId, settings}));
    },
    [dispatch, elementId, componentId]
  );

  const fetchUiState = useDesignerSelector(uiStateSelector);
  const updateUiState = React.useCallback(
    (uiState: {isWebflowConfigured?: boolean; isEmailConfigured?: boolean}) => {
      dispatch(formSettingsUiStateUpdated({elementId, componentId, uiState}));
    },
    [dispatch, elementId, componentId]
  );

  // Fetch settings on mount if not already loaded
  React.useEffect(() => {
    if (!formSettings && !isFetching && !error) {
      fetchSettings();
    }
  }, [formSettings, isFetching, error, fetchSettings]);

  return {
    formSettings,
    isFetching,
    isUpdating,
    updateState,
    error,
    fetchSettings,
    updateSettings,
    fetchUiState,
    updateUiState,
  };
};
